import {html, LitElement, nothing} from "lit";
import {customElement, query, state} from "lit/decorators.js";
import "../../common/controls-inputs/Button.js";
import "../../common/controls-inputs/LabeledTextInput.js";
import {renderPowerNotesLogo} from "../../common/powerNotesLogo.js";
import Data from "../../../main/data/index.js";
import {emailValidators, validate} from "../../../main/utils/form.js";
import {Modal} from "../../common/modals.js";
import {modalStyles} from "../../common/styles/Modals.js";

@customElement("pn-int-account-modal")
export class IntegrationAccountModal extends LitElement {
	@state() name = "";
	@state() email = "";
	@state() success: string | boolean = false;
	@state() error:   string | boolean = false;
	@state() fieldErrors = {
		name:  false,
		email: false
	};

	@query(".modal-content") modalContent: HTMLElement;

	modal: Modal | null = null;

	static styles = [modalStyles];

	constructor() {
		super();

		this.name = Data.app.me?.name ?? "";
		this.email = Data.app.me?.email ?? "";
	}

	firstUpdated() {
		this.modal = new Modal({
			id:               "integration-account",
			modalContent:     this.modalContent,
			parent:           this.shadowRoot,
			isSingleUseModal: true
		});
		this.modal.show();
	}

	validateForm() {
		this.fieldErrors = {
			email: validate(this.email, emailValidators),
			name:  this.name.trim().length === 0
		};

		return !!this.fieldErrors.email || !!this.fieldErrors.name;
	}

	onClickResetPassword = async () => {
		const encodedEmail = encodeURIComponent(Data.app.me?.email ?? "");
		if (!encodedEmail)
			throw new Error("No email address in account.");

		try {
			await Data.app.backend.callApi({
				url:                `account/${encodedEmail}/reset-password`,
				method:             "PUT",
				expectedStatusCode: 204,
				errorMessage:       "Unknown error sending password reset email."
			});
			this.success = "Password reset sent. Please check your email.";
			this.error = false;
		} catch (e) {
			console.error(e);
			this.success = false;
			this.error = e.message;
		}
	};

	onClickUpdate = async () => {
		const hasErrors = this.validateForm();
		if (hasErrors)
			return;

		const updates = {
			name:  this.name,
			email: this.email
		};

		try {
			if (!Data.app.me)
				throw new Error("Unknown error");
			const result = await Data.account.update(Data.app.me.uuid, updates);
			this.success = result.re_verification_email_sent ? "Account updated successfully. Please check your email and follow the instructions to verify your new address." : "Account updated successfully.";
			this.error = false;
		} catch (e) {
			console.error(e);
			this.success = false;
			this.error = e.message;
		}
	};

	keydownOnForm = (event: KeyboardEvent) => {
		if (event.key === "Enter") {
			event.preventDefault();

			this.onClickUpdate();
		}
	};

	renderLogo() {
		return html`<div id="pn-modal-logo">${renderPowerNotesLogo()}<div>`;
	}

	renderForm() {
		return html`
			<div class="heading"><h2>${this.renderLogo()} Account</h2></div>

			<div class="body">
				<p>Login information</p>
				<form>
					<pn-labeled-text-input
						autocomplete="name"
						.error=${this.fieldErrors.name}
						id="pn-account-name"
						inputType="text"
						label="Name"
						.required=${true}
						.onInput=${(e: {target: {value: string}}) => {
							this.name = e.target.value.trim();
						}}
						.onKeydown=${this.keydownOnForm}
						.value=${this.name}
					></pn-labeled-text-input>
					<pn-labeled-text-input
						autocomplete="email"
						.error=${this.fieldErrors.email}
						id="pn-account-email"
						inputType="email"
						label="Email"
						.required=${true}
						.onInput=${(e: {target: {value: string}}) => {
							this.email = e.target.value.trim();
						}}
						.onKeydown=${this.keydownOnForm}
						.value=${this.email}
					></pn-labeled-text-input>
					<pn-button
						.onClick=${this.onClickResetPassword}
						renderType="link-blue"
					>Change your password</pn-button>
				</form>
			</div>

			<div class="buttons">
				<pn-button
					.onClick=${this.onClickUpdate}
					renderType="auth"
				>Update</pn-button>
				
				${this.success && this.success !== true ? html`<div id="modal-success">
					<p class="modal-success-msg msg-success-with-bg">${this.success}</p>
				</div>` : nothing}
				
				${this.error ? html`<div id="modal-error">
					<p class="modal-error-msg msg-error-with-bg">${this.error === true ? "Unknown error" : this.error}</p>
				</div>` : nothing}
			</div>
		`;
	}

	render() {
		return html`
			<div class="modal-content">
				<div id="pn-modal-content">
					${this.renderForm()}
				</div>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"pn-pn-int-account-modal": IntegrationAccountModal;
	}
}
